'use client';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { switchClasses } from '@mui/material';

let theme = createTheme({
    cssVariables: true,
    typography: {
        fontFamily: 'var(--font-outfit)'
    },
    palette: {
        primary: {
            main: '#216ECB',
        },
        secondary: {
            main: '#FC4ABA',
        },
        error: {
            main: '#BA1A1A',
        }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    padding: '10px 24px',
                    lineHeight: '20px',
                    variants: [
                        {
                            props: { variant: 'outlined' },
                            style: {
                                borderColor: 'var(--mui-palette-outline-opacity012)'
                            }
                        }
                    ],
                    '&.Mui-disabled': {
                        variants: [
                            {
                                props: { variant: 'outlined' },
                                style: {
                                    color: 'var(--mui-palette-common-onBackground)'
                                }
                            },
                            {
                                props: { variant: 'contained' },
                                style: {
                                    backgroundColor: 'var(--mui-palette-outline-opacity012)',
                                    color: 'var(--mui-palette-common-onBackground)',
                                    boxShadow: 'none'
                                }
                            }
                        ]
                    }
                }
            }
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backdropFilter: 'blur(25px) brightness(100%)'
                },
                invisible: {
                    backdropFilter: 'none'
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    backgroundColor: 'white',
                    padding: '0 10px',
                    borderRadius: '2px'
                }
            }
        },
        MuiFormGroup: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '30px'
                }
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: '100%'
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: 0
                },
                label: {
                    fontSize: '0.8rem'
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    display: 'inline-flex',
                    boxSizing: 'border-box',
                    position: 'relative',
                    flexShrink: 0,
                    zIndex: 0,
                    verticalAlign: 'middle',
                    width: '3.3rem',
                    height: '2rem',
                    padding: '0',
                    margin: '0 30px 0 0',
                    overflow: 'unset'
                },
                switchBase: {
                    padding: '0.35rem',
                    [`&.${switchClasses.checked}`]: {
                        color: 'white',
                        transform: 'translateX(calc(1.3rem))',
                        [`& + .${switchClasses.track}`]: {
                            backgroundColor: '#216ECB',
                            opacity: 1,
                            border: 'none'
                        },
                        [`& .${switchClasses.thumb}`]: {
                            backgroundColor: 'white',
                        },
                    },
                    [`& .${switchClasses.thumb}`]: {
                        boxShadow: 'none',
                        backgroundColor: 'var(--mui-palette-surface-variant)',
                        width: '1.3rem',
                        height: '1.3rem'
                    },
                },
                [`& .${switchClasses.track}`]: {
                    borderRadius: 40,
                    border: 'solid var(--mui-palette-surface-variant)}',
                    borderWidth: '2px',
                    backgroundColor: 'var(--mui-palette-secondary-container)',
                    opacity: 1,
                    transition: 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1), border 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                    boxSizing: 'border-box',
                },
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    borderRadius: '5px',
                    fontSize: '1rem',
                    fieldset: {
                        borderRadius: '5px',
                    },
                    '&::placeholder': {
                        color: 'var(--mui-palette-onsurface-variant)',
                        fontWeight: 300
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderColor: 'var(--mui-palette-outline-opacity012)',
                    '& fieldset': {
                        borderColor: 'var(--mui-palette-outline-opacity012)'
                    },
                    fontSize: '0.9rem',
                    fontWeight: 300
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: 'var(--mui-palette-onsurface-variant)',
                    fontWeight: 300,
                    fontSize: '0.9rem',
                    top: '-4px'
                },
                shrink: {
                    top: 0
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontSize: '0.9rem'
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: 'var(--mui-palette-onsurface-variant)',
                    fontWeight: 300,
                    fontSize: '0.9rem'
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    maxWidth: 'none'
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '0 24px 20px 24px'
                }
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    borderRadius: '5px'
                }
            }
        },
        MuiAccordionSummary: {
            styleOverrides: {
                content: {
                    textAlign: 'start',
                    display: 'inline-block'
                }
            }
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    textAlign: 'start',
                    fontWeight: '300',
                    color: 'var(--mui-palette-outline-opacity080)'
                }
            }
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: 'black'
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                    textDecoration: 'none'
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: '5px'
                }
            }
        }
    }
  });
  
theme = responsiveFontSizes(theme);

export default theme;